import React, { useState, useMemo } from 'react';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { logError } from 'utils';
import { isRetexAwatingAtAdmin } from 'utils/methods';
import { CustomToast, UiKit } from 'components';
import { getRetexActionData } from 'views/Requests/methods';

/** @type {Record<string, import('utils/types').ButtonVariant>} */
const BUTTON_TYPE_VARIANT_MAP = {
  info: 'outline',
  success: 'secondary',
  danger: 'destructive'
};

export default function RetexAction({
  data = {},
  isCompact = false,
  isApproving = true,
  isRestocking = true,
  comment = '',
  customComment = '',
  isDisabled = false,
  onBeforeProcessing = () => {},
  onAfterProcessing = () => {}
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const status = _get(data, 'status', '');

  const {
    show,
    text,
    type,
    alertModalData,
    toastMessage,
    onTrigger
  } = useMemo(
    () =>
      getRetexActionData(
        data,
        isApproving,
        isRestocking,
        comment,
        customComment
      ),
    [data, isApproving, isRestocking, comment, customComment]
  );

  const onStartProcessing = () => {
    const isValidAction = isRetexAwatingAtAdmin(status);
    if (!isValidAction) return;

    setIsProcessing(true);
    onBeforeProcessing();
    onTrigger(
      data => {
        onAfterProcessing(null, data);
        const type = isApproving ? 'success' : 'info';
        const msg = _get(toastMessage, type, '');
        if (msg) CustomToast({ type, msg });
      },
      err => {
        logError(err);
        onAfterProcessing(err, null);
        CustomToast({
          isNotified: err.notified,
          type: 'error',
          msg: _get(toastMessage, 'error', false)
        });
      },
      () => setIsProcessing(false)
    );
  };

  const onStartProcess = () => {
    if (alertModalData) return setShowAlert(true);
    onStartProcessing();
  };

  const onCloseAlertModal = () => setShowAlert(false);

  const alertModalTitle = _get(alertModalData, 'title', '');
  const alertModalDescription = _get(alertModalData, 'description', '');
  const alertModalButtonText = _get(alertModalData, 'buttonText', '');

  if (!show) return null;

  return (
    <>
      <UiKit.Button
        size={isCompact ? 'sm' : 'lg'}
        variant={BUTTON_TYPE_VARIANT_MAP[type] || 'outline'}
        onClick={onStartProcess}
        disabled={isDisabled}
        processing={isProcessing}
        data-type={type}
        data-approval={isApproving}
        className={styles.button}
      >
        {text}
      </UiKit.Button>
      <UiKit.Modal
        isOpen={showAlert}
        onClose={onCloseAlertModal}
        isProcessing={isProcessing}
        className={styles.alertModal}
      >
        <UiKit.Modal.Header title={alertModalTitle} />
        <UiKit.Modal.Body>
          <p className={styles.alertDescription}>{alertModalDescription}</p>
        </UiKit.Modal.Body>
        <UiKit.Modal.Footer
          onConfirm={onStartProcessing}
          confirmBtnVariant="destructive"
          confirmBtnText={alertModalButtonText}
        />
      </UiKit.Modal>
    </>
  );
}
